import { RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ReadinessReport } from '@/features/generation-readiness/check-readiness'
import { ReadinessItemRow } from '@/features/generation-readiness/readiness-item-row'
import type { ReadinessItem } from '@/features/generation-readiness/translate-gaps'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import {
  localizeCompactMachineValidationReason,
  localizeCompactReadinessItem,
} from './compact-generation-readiness-localization'

const KIND_ORDER: readonly ReadinessItem['kind'][] = [
  'blocking',
  'unfilled',
  'advisory',
]

interface CompactGenerationReadinessCopy {
  eyebrow: string
  checkFailed: string
  blocked: (count: number) => string
  verifiedScopeClear: string
  ready: string
  kindLabel: Record<ReadinessItem['kind'], string>
  retry: string
  generate: string
  details: (count: number) => string
}

const COPY: Record<LocaleCode, CompactGenerationReadinessCopy> = {
  en: {
    eyebrow: 'Generation readiness',
    checkFailed: 'Readiness check could not be completed',
    blocked: (count) => `${count} ${count === 1 ? 'issue' : 'issues'} to resolve first`,
    verifiedScopeClear: 'No blockers in the verified scope',
    ready: 'Ready to generate',
    kindLabel: {
      blocking: 'Blocking',
      unfilled: 'Undecided',
      advisory: 'Advice',
    },
    retry: 'Try again',
    generate: 'Generate',
    details: (count) => `View ${count} ${count === 1 ? 'detail' : 'details'}`,
  },
  ko: {
    eyebrow: '생성 준비 상태',
    checkFailed: '점검을 완료하지 못했습니다',
    blocked: (count) => `먼저 해결할 항목 ${count}개`,
    verifiedScopeClear: '확인된 범위에서 막는 항목이 없습니다',
    ready: '생성할 준비가 됐습니다',
    kindLabel: {
      blocking: '막는 항목',
      unfilled: '정하지 않은 항목',
      advisory: '권고',
    },
    retry: '다시 시도',
    generate: '생성하기',
    details: (count) => `세부 항목 ${count}개 보기`,
  },
  ja: {
    eyebrow: '生成準備状況',
    checkFailed: '準備状況の確認を完了できませんでした',
    blocked: (count) => `先に解決する項目 ${count}件`,
    verifiedScopeClear: '確認済みの範囲にブロック項目はありません',
    ready: '生成の準備ができました',
    kindLabel: {
      blocking: 'ブロック項目',
      unfilled: '未決定',
      advisory: '推奨',
    },
    retry: '再試行',
    generate: '生成する',
    details: (count) => `詳細 ${count}件を表示`,
  },
  zh: {
    eyebrow: '生成准备状态',
    checkFailed: '无法完成准备状态检查',
    blocked: (count) => `需要先解决 ${count} 项`,
    verifiedScopeClear: '已检查范围内没有阻塞项',
    ready: '已准备好生成',
    kindLabel: {
      blocking: '阻塞项',
      unfilled: '未决定项',
      advisory: '建议',
    },
    retry: '重试',
    generate: '生成',
    details: (count) => `查看 ${count} 个详细项`,
  },
  'zh-TW': {
    eyebrow: '產生準備狀態',
    checkFailed: '無法完成準備狀態檢查',
    blocked: (count) => `需要先解決 ${count} 項`,
    verifiedScopeClear: '已檢查範圍內沒有阻擋項目',
    ready: '已準備好產生',
    kindLabel: {
      blocking: '阻擋項目',
      unfilled: '未決定項目',
      advisory: '建議',
    },
    retry: '再試一次',
    generate: '產生',
    details: (count) => `查看 ${count} 個詳細項目`,
  },
}

function groupByKind(
  items: readonly ReadinessItem[],
): Record<ReadinessItem['kind'], ReadinessItem[]> {
  const groups: Record<ReadinessItem['kind'], ReadinessItem[]> = {
    blocking: [],
    unfilled: [],
    advisory: [],
  }
  for (const item of items) groups[item.kind].push(item)
  return groups
}

function readinessHeadline(
  report: ReadinessReport,
  blockingCount: number,
  copy: CompactGenerationReadinessCopy,
): string {
  if (report.status === 'error') return copy.checkFailed
  if (blockingCount > 0) return copy.blocked(blockingCount)
  if (!report.machineValidation.performed) {
    return copy.verifiedScopeClear
  }
  return copy.ready
}

/**
 * Narrow-rail presentation for Chat only. It preserves the shared report and
 * its fail-closed proceed rule while keeping the verdict and primary action in
 * the first viewport. Every report item remains available under details.
 */
export function CompactGenerationReadinessPanel({
  locale,
  report,
  onProceed,
  onRetry,
}: {
  locale: LocaleCode
  report: ReadinessReport
  onProceed: () => void
  onRetry: () => void
}) {
  const copy = COPY[locale] ?? COPY.en
  const localizedItems = report.items.map((item) =>
    localizeCompactReadinessItem(item, locale, report.status),
  )
  const machineValidationReason = localizeCompactMachineValidationReason(
    report,
    locale,
  )
  const groups = groupByKind(localizedItems)
  const canProceed =
    report.status === 'success' && groups.blocking.length === 0
  const priorityItems = groups.blocking.slice(0, 2)
  const priorityItemSet = new Set(priorityItems)
  const detailGroups = groupByKind(
    localizedItems.filter((item) => !priorityItemSet.has(item)),
  )
  const detailCount = KIND_ORDER.reduce(
    (count, kind) => count + detailGroups[kind].length,
    0,
  )

  return (
    <section
      data-testid="generation-readiness-panel"
      data-status={report.status}
      className="flex flex-col gap-3 rounded-lg border border-border bg-card px-3 py-3"
    >
      <header
        className="flex items-start justify-between gap-3"
        data-testid={
          report.status === 'error' ? 'generation-readiness-error' : undefined
        }
        role={report.status === 'error' ? 'alert' : undefined}
      >
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-muted-foreground">
            {copy.eyebrow}
          </p>
          <h2 className="mt-0.5 text-sm font-semibold leading-5 text-foreground">
            {readinessHeadline(report, groups.blocking.length, copy)}
          </h2>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
            {copy.kindLabel.blocking} {groups.blocking.length} ·{' '}
            {copy.kindLabel.unfilled} {groups.unfilled.length} ·{' '}
            {copy.kindLabel.advisory} {groups.advisory.length}
          </p>
        </div>

        {report.status === 'error' ? (
          <div className="shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
            >
              <RotateCcw aria-hidden />
              {copy.retry}
            </Button>
          </div>
        ) : canProceed ? (
          <Button
            type="button"
            size="sm"
            data-testid="generation-readiness-proceed-button"
            onClick={onProceed}
            className="shrink-0"
          >
            {copy.generate}
          </Button>
        ) : null}
      </header>

      {machineValidationReason ? (
        <p
          data-testid="generation-readiness-machine-validation-note"
          className="line-clamp-2 text-[11px] leading-4 text-muted-foreground"
          title={machineValidationReason}
        >
          {machineValidationReason}
        </p>
      ) : null}

      {priorityItems.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {priorityItems.map((item, index) => (
            <ReadinessItemRow key={`priority-blocking-${index}`} item={item} />
          ))}
        </ul>
      ) : null}

      {detailCount > 0 ? (
        <details className="group rounded-md border border-border px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium text-foreground marker:text-muted-foreground">
            {copy.details(detailCount)}
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            {KIND_ORDER.filter((kind) => detailGroups[kind].length > 0).map(
              (kind) => (
                <div key={kind} className="flex flex-col gap-2">
                  <h3 className="text-[11px] font-semibold text-muted-foreground">
                    {copy.kindLabel[kind]} ({detailGroups[kind].length})
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {detailGroups[kind].map((item, index) => (
                      <ReadinessItemRow
                        key={`${kind}-detail-${index}`}
                        item={item}
                      />
                    ))}
                  </ul>
                </div>
              ),
            )}
          </div>
        </details>
      ) : null}
    </section>
  )
}
